import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link as MuiLink,
  Theme,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import { Link } from "react-router-dom";

import { monospaceStyle, panelShadow } from "../../../theme";
import {
  MissionCapability,
  MissionCategory,
  MissionCentre,
  MissionEdge,
  Missions,
} from "../../../types/analytics";
import { EntityClickHandler, getEntityTypeEntry } from "../../../types/entityRegistry";
import {
  AnalyticsSelection,
  DEFAULT_ANALYTICS_STATE,
  exploreAddress,
  selectionOf,
} from "../analyticsState";
import { counted } from "../Ecosystem/EcosystemParts";
import {
  LevelMark,
  capabilityName,
  categoryName,
  liftText,
  percent,
} from "./MissionsParts";

// What the panel shows: a capability or a category of the rings
export type MissionsPick =
  | { kind: "capability"; id: string }
  | { kind: "category"; id: string };

// The selection that opens a category's products in Explore: the category
// alone. Its providers stand outside the scope (parts inside the hosts, or
// products anywhere in the graph), so the scope's filters are not kept.
// The products under no kind have no category to pick.
export const categorySelection = (category: MissionCategory): AnalyticsSelection | null =>
  category.other ? null : { ...selectionOf(DEFAULT_ANALYTICS_STATE), category: [category.id] };

// The selection that opens the scope's products said to have a capability:
// the scope, its capability picks replaced by the capability
export const capabilitySelection = (
  selection: AnalyticsSelection,
  capability: MissionCapability,
): AnalyticsSelection => ({ ...selectionOf(selection), capability: [capability.capability.id] });

// The selection that opens the centre's hosts: the scope with the centre as
// its capability pick, and its qualifiers as the condition and mode picks
// (one claim must meet the three, as the centre's hosts are counted).
// Explore's picks of one filter are alternatives, so this is the centre's
// hosts only when the scope picks no capability, condition or mode of its
// own; otherwise null.
export const hostsSelection = (
  selection: AnalyticsSelection,
  centre: Pick<MissionCentre, "capability" | "condition" | "mode">,
): AnalyticsSelection | null =>
  selection.capability.length > 0 || selection.condition.length > 0 || selection.mode.length > 0
    ? null
    : {
        ...selectionOf(selection),
        capability: [centre.capability.id],
        condition: centre.condition ? [centre.condition.id] : [],
        mode: centre.mode ? [centre.mode.id] : [],
      };

// A share difference in points: +32 points, −5 points, 0 points
export const differenceText = (difference: number): string => {
  const points = Math.round(difference * 100);
  return `${points > 0 ? "+" : points < 0 ? "−" : ""}${Math.abs(points)} ${Math.abs(points) === 1 ? "point" : "points"}`;
};

// A small uppercase head, as the fields of the designs carry
const HEAD_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
} as const;

// An action that reads as a link
const ACTION_SX = {
  p: 0,
  minWidth: 0,
  textTransform: "none",
  fontSize: "12.5px",
  fontWeight: 400,
  lineHeight: 1.5,
  justifyContent: "flex-start",
  textAlign: "left",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
} as const;

const SMALL_CHIP_SX = { height: 20, fontSize: "11.5px" } as const;

const MONO = { ...monospaceStyle, fontSize: "12px" } as const;

// A stated "enables", in teal: the only claim that says a part makes the
// capability possible
export const EnablesChip: React.FC<{ count: number }> = ({ count }) => (
  <Box
    component="span"
    data-testid="enables-chip"
    sx={(theme: Theme) => ({
      display: "inline-flex",
      alignItems: "center",
      height: 18,
      px: "6px",
      borderRadius: "5px",
      border: `1px solid ${alpha(theme.palette.chart.enables, 0.5)}`,
      color: theme.palette.chart.enables,
      backgroundColor: alpha(theme.palette.chart.enables, 0.08),
      fontSize: 11,
      whiteSpace: "nowrap",
    })}
  >
    enables · {count}
  </Box>
);

// A dashed chip: a category that could provide, with none on record inside
const CouldChip: React.FC = () => (
  <Chip label="could provide" size="small" variant="outlined" sx={{ ...SMALL_CHIP_SX, borderStyle: "dashed" }} />
);

// A category's name: a link to its products in Explore, plain for the
// products under no kind
const CategoryLink: React.FC<{ category: MissionCategory; quiet?: boolean }> = ({
  category,
  quiet = false,
}) => {
  const picked = categorySelection(category);
  const name = categoryName(category);
  return picked ? (
    <MuiLink
      component={Link}
      to={exploreAddress(picked)}
      underline="hover"
      sx={quiet ? { color: "text.secondary" } : undefined}
    >
      {name}
    </MuiLink>
  ) : (
    <span>{name}</span>
  );
};

// Whether the detail overlay can show an entity of this type: a capability
// on no row of the tree (type `capability`) and the "other" bucket cannot
const opensInOverlay = (type: string): boolean => getEntityTypeEntry(type) !== undefined;

const PanelFrame: React.FC<{
  label: string;
  title: string;
  badge: React.ReactNode;
  onClose: () => void;
  // Opens the item's detail; without it the title is plain text
  onOpen?: () => void;
  children: React.ReactNode;
}> = ({ label, title, badge, onClose, onOpen, children }) => (
  <Box
    component="aside"
    aria-label={label}
    sx={{
      position: "absolute",
      top: 14,
      right: 14,
      zIndex: 5,
      width: 320,
      maxWidth: "calc(100% - 28px)",
      maxHeight: "calc(100% - 28px)",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: "9px",
      p: "14px 16px",
      overflow: "auto",
      fontSize: "12.5px",
      border: 1,
      borderColor: "divider",
      borderRadius: (theme) => `${theme.radius.surface}px`,
      backgroundColor: "background.raised",
      boxShadow: (theme) => panelShadow(theme),
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <Typography
        component="h3"
        sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.45, overflowWrap: "anywhere" }}
      >
        {/* The name opens the item's detail, as the ecosystem panel's does */}
        {onOpen ? (
          <Button
            size="small"
            title="Open the detail"
            onClick={onOpen}
            sx={{
              ...ACTION_SX,
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: "inherit",
              color: "text.primary",
              display: "inline",
              verticalAlign: "baseline",
            }}
          >
            {title}
          </Button>
        ) : (
          title
        )}
      </Typography>
      {badge}
      <IconButton size="small" aria-label="Clear selection" onClick={onClose} sx={{ m: "-4px" }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
    {children}
  </Box>
);

const SelectedChip: React.FC = () => (
  <Chip
    label="selected"
    size="small"
    sx={(theme: Theme) => ({
      ...SMALL_CHIP_SX,
      border: `1px solid ${alpha(theme.palette.accent.main, 0.45)}`,
      color: theme.palette.chart.accentText,
      backgroundColor: alpha(theme.palette.accent.main, 0.1),
    })}
  />
);

// One row of a list: what it is and a figure on the right, a line under
const ProviderRow: React.FC<{
  name: React.ReactNode;
  figure: string;
  quiet?: boolean;
  children: React.ReactNode;
}> = ({ name, figure, quiet = false, children }) => (
  <Box
    component="li"
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      columnGap: "10px",
      rowGap: "2px",
      alignItems: "baseline",
      py: "6px",
      borderTop: 1,
      borderColor: "divider",
    }}
  >
    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      {name}
    </Box>
    <Box component="span" sx={{ ...MONO, color: quiet ? "text.secondary" : "text.primary" }}>
      {figure}
    </Box>
    <Box
      component="span"
      sx={{
        gridColumn: "1 / -1",
        fontSize: "11.5px",
        color: "text.secondary",
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
      }}
    >
      {children}
    </Box>
  </Box>
);

const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

interface CapabilityPanelProps {
  missions: Missions;
  capability: MissionCapability;
  selection: AnalyticsSelection;
  onSelect: (pick: MissionsPick | null) => void;
  // Puts a capability at the centre
  onCentre: (id: string) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected capability (or mission): where it is filed, how
// many of the centre's hosts claim it against the comparable products, and,
// for a capability, who provides it
export const CapabilityPanel: React.FC<CapabilityPanelProps> = ({
  missions,
  capability,
  selection,
  onSelect,
  onCentre,
  onOpenEntity,
}) => {
  const centre = missions.centre;
  const centreHosts = centre?.hosts ?? 0;
  const mission = capability.level === "operator";
  const byId = new Map(missions.categories.map((category) => [category.id, category]));
  const edges = missions.edges.filter((edge) => edge.capability === capability.id);
  // On record inside first, the most hosts first; then those that could
  const ordered = [
    ...edges.filter((edge) => edge.provider === "inside").sort((a, b) => b.hosts - a.hosts),
    ...edges.filter((edge) => edge.provider === "could").sort((a, b) => b.products - a.products),
  ];
  const hosts = centre ? hostsSelection(selection, centre) : null;
  return (
    <PanelFrame
      label={mission ? "Selected mission" : "Selected capability"}
      title={capabilityName(capability)}
      badge={<SelectedChip />}
      onClose={() => onSelect(null)}
      onOpen={
        opensInOverlay(capability.capability.type)
          ? () => onOpenEntity(capability.capability.id, capability.capability.type)
          : undefined
      }
    >
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        <Chip
          size="small"
          variant="outlined"
          sx={SMALL_CHIP_SX}
          label={
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <LevelMark level={capability.level} small />
              {mission ? "mission" : capability.level}
            </Box>
          }
        />
        {capability.function_area && (
          <Chip size="small" variant="outlined" sx={SMALL_CHIP_SX} label={capability.function_area.name ?? capability.function_area.id} />
        )}
        {capability.effect_area && (
          <Chip size="small" variant="outlined" sx={SMALL_CHIP_SX} label={capability.effect_area.name ?? capability.effect_area.id} />
        )}
      </Box>

      <Box
        component="dl"
        sx={{
          m: 0,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: "12px",
          rowGap: "5px",
          lineHeight: 1.45,
          "& dt": { color: "text.secondary" },
          "& dd": { m: 0 },
        }}
      >
        <dt>The hosts</dt>
        <dd aria-label="The hosts">
          <Mono>{capability.centre_hosts}</Mono> of the <Mono>{centreHosts}</Mono> hosts claim it ·{" "}
          <Mono>{percent(capability.centre_share)}</Mono>
        </dd>
        <dt>Comparable</dt>
        <dd aria-label="Comparable products">
          <Mono>{percent(capability.baseline_share)}</Mono> of the <Mono>{missions.baseline}</Mono> comparable
          products (the scope&apos;s products of the same categories) · lift{" "}
          <Box component="span" sx={{ ...monospaceStyle, color: (theme) => theme.palette.chart.accentText }}>
            {liftText(capability.lift)}
          </Box>
        </dd>
        <dt>Difference</dt>
        <dd aria-label="Share difference">
          <Mono>{differenceText(capability.share_difference)}</Mono> of share: what the ring is ranked by
        </dd>
      </Box>

      {!mission && <Box sx={{ ...HEAD_SX, mt: 0.5 }}>Who provides it</Box>}
      {mission ? null : ordered.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No category on record inside these hosts or claiming it elsewhere, among those drawn.
        </Typography>
      ) : (
        <Box component="ul" aria-label="Providers" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {ordered.map((edge: MissionEdge) => {
            const category = byId.get(edge.category);
            if (!category) {
              return null;
            }
            return edge.provider === "inside" ? (
              <ProviderRow
                key={`${edge.provider}:${edge.category}`}
                name={<CategoryLink category={category} />}
                figure={`${edge.hosts} inside`}
              >
                <span>
                  in <Mono>{edge.hosts}</Mono> of the <Mono>{centreHosts}</Mono> hosts ·{" "}
                  <Mono>{counted(edge.products, "part")}</Mono> · <Mono>{counted(edge.makers, "maker")}</Mono>
                </span>
                {edge.enables > 0 && <EnablesChip count={edge.enables} />}
              </ProviderRow>
            ) : (
              <ProviderRow
                key={`${edge.provider}:${edge.category}`}
                name={
                  <>
                    <CategoryLink category={category} quiet />
                    <CouldChip />
                  </>
                }
                figure="0 inside"
                quiet
              >
                <span>
                  <Mono>{counted(edge.products, "product")}</Mono> claim it ·{" "}
                  <Mono>{counted(edge.makers, "maker")}</Mono>; none is on record inside these hosts
                </span>
              </ProviderRow>
            );
          })}
        </Box>
      )}

      {!mission && (
        <Typography variant="caption" component="div" color="text.disabled" aria-label="Claimed alone">
          <Mono>{capability.alone}</Mono> of the <Mono>{capability.centre_hosts}</Mono> hosts claim it with no
          part on record inside that has or enables it: the platform claims it alone
        </Typography>
      )}

      <Button
        variant="outlined"
        size="small"
        onClick={() => onCentre(capability.capability.id)}
        sx={{ alignSelf: "flex-start", textTransform: "none", fontSize: "12.5px" }}
      >
        Put it at the centre
      </Button>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {hosts && centre && (
          <MuiLink component={Link} to={exploreAddress(hosts)} underline="hover">
            Open the {counted(centre.hosts, "host")}
          </MuiLink>
        )}
        <MuiLink component={Link} to={exploreAddress(capabilitySelection(selection, capability))} underline="hover">
          The scope&apos;s products that claim it
        </MuiLink>
      </Box>
      <Typography variant="caption" component="div" color="text.disabled">
        Click selects; a click on the canvas clears. Pick another centre above.
      </Typography>
    </PanelFrame>
  );
};

interface CategoryPanelProps {
  missions: Missions;
  category: MissionCategory;
  onSelect: (pick: MissionsPick | null) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected category: its counts, and the capabilities it
// provides with the counts of each edge
export const CategoryPanel: React.FC<CategoryPanelProps> = ({
  missions,
  category,
  onSelect,
  onOpenEntity,
}) => {
  const byId = new Map(missions.capabilities.map((capability) => [capability.id, capability]));
  const edges = missions.edges.filter((edge) => edge.category === category.id);
  const picked = categorySelection(category);
  const missionHosts = missions.centre?.hosts ?? 0;
  return (
    <PanelFrame
      label="Selected category"
      title={categoryName(category)}
      badge={
        category.inside ? (
          <Chip label="on record inside" size="small" variant="outlined" sx={SMALL_CHIP_SX} />
        ) : (
          <CouldChip />
        )
      }
      onClose={() => onSelect(null)}
      onOpen={
        !category.other && opensInOverlay(category.category.type)
          ? () => onOpenEntity(category.category.id, category.category.type)
          : undefined
      }
    >
      <Typography variant="caption" component="div" sx={monospaceStyle}>
        {category.nature ? `${category.nature.replace(/_/g, " ")} · ` : ""}
        {category.inside
          ? `inside ${category.inside_hosts} of the ${missionHosts} hosts · `
          : "could provide · "}
        {counted(category.products, "product")} · {counted(category.makers, "maker")}
      </Typography>
      {category.thin && (
        <Typography variant="caption" component="div" color="text.disabled">
          Under the floor of {counted(missions.floors.min_support, "product")}: a hint, not a finding.
        </Typography>
      )}
      <Box sx={{ ...HEAD_SX, mt: 0.5 }}>What it provides</Box>
      <Box component="ul" aria-label="Capabilities provided" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {edges.map((edge) => {
          const capability = byId.get(edge.capability);
          if (!capability) {
            return null;
          }
          return (
            <ProviderRow
              key={`${edge.provider}:${edge.capability}`}
              name={
                <>
                  <LevelMark level={capability.level} small />
                  <Button
                    size="small"
                    onClick={() => onSelect({ kind: "capability", id: capability.id })}
                    sx={ACTION_SX}
                  >
                    {capabilityName(capability)}
                  </Button>
                </>
              }
              figure={edge.provider === "inside" ? `${edge.hosts} inside` : "could"}
              quiet={edge.provider === "could"}
            >
              {edge.provider === "inside" ? (
                <span>
                  in <Mono>{edge.hosts}</Mono> of the <Mono>{missionHosts}</Mono> hosts ·{" "}
                  <Mono>{counted(edge.products, "part")}</Mono> · <Mono>{counted(edge.makers, "maker")}</Mono>
                </span>
              ) : (
                <span>
                  <Mono>{counted(edge.products, "product")}</Mono> claim it ·{" "}
                  <Mono>{counted(edge.makers, "maker")}</Mono>; none on record inside these hosts
                </span>
              )}
              {edge.enables > 0 && <EnablesChip count={edge.enables} />}
            </ProviderRow>
          );
        })}
      </Box>
      {picked && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <MuiLink component={Link} to={exploreAddress(picked)} underline="hover">
            Its products in Explore
          </MuiLink>
        </Box>
      )}
    </PanelFrame>
  );
};
